import type { AdminHeyReachBlocker } from '@workspace/api-backend'

interface HeyReachBlockersProps {
  blockers: AdminHeyReachBlocker[]
  /**
   * Startups actually selected for deletion. A blocker for an unselected
   * startup is shown but does not gate the button — that startup is not being
   * deleted, so its workspace is not this deletion's problem yet.
   */
  selectedStartupIds?: string[]
}

/**
 * What the admin must remove in HeyReach before a deletion is allowed.
 *
 * Rendered as soon as the dialog opens rather than after the startup is ticked:
 * the whole point is to tell the admin what work is waiting before they commit
 * to anything. HeyReach has no API to delete a workspace or revoke an org seat,
 * so this is manual work, and the panel carries the identifiers needed to do it
 * — workspace id and invited email — plus a way to get there.
 */
export function HeyReachBlockers({ blockers, selectedStartupIds }: HeyReachBlockersProps) {
  if (blockers.length === 0) return null

  const gates = (b: AdminHeyReachBlocker) =>
    selectedStartupIds === undefined || selectedStartupIds.includes(b.startupId)

  const anyGating = blockers.some(gates)

  return (
    <div
      className={`rounded border px-3 py-2 text-sm ${
        anyGating ? 'border-red-500/30 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'
      }`}
    >
      <p className={`font-medium ${anyGating ? 'text-red-400' : 'text-amber-500'}`}>
        {anyGating ? 'Blocked — clear these in HeyReach first' : 'Needs HeyReach cleanup first'}
      </p>
      <ul className="mt-1.5 space-y-1.5">
        {blockers.map((b) => (
          <li key={`${b.startupId}-${b.kind}`} className="text-muted-foreground">
            <span className="text-foreground">{b.startupName}</span> — {b.action}
            {!gates(b) && <span className="text-xs"> (only blocks if you select it above)</span>}
            <span className="mt-0.5 block text-xs">
              {b.workspaceId !== null && <>workspace {b.workspaceId}</>}
              {b.workspaceId !== null && b.invitedEmail ? ' · ' : null}
              {b.invitedEmail}
              {' · '}
              <a
                href={b.appUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:opacity-80"
              >
                Open HeyReach
              </a>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
